import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Path, Defs, LinearGradient as SvgLinearGradient, Stop } from 'react-native-svg';
import { Trophy, Star, Flame, Zap, Shield, Crown } from 'lucide-react-native';
import {
  CREST_COLORS,
  type CrestColorId,
  type CrestEmblemId,
} from './CreateWorldCupGroupScreen/CreateWorldCupGroupScreen.constants';

type IconProps = { size: number; color: string; strokeWidth: number };

const EMBLEM_ICONS: Record<CrestEmblemId, React.FC<IconProps>> = {
  trophy: (p) => <Trophy {...p} />,
  star: (p) => <Star {...p} />,
  flame: (p) => <Flame {...p} />,
  lightning: (p) => <Zap {...p} />,
  shield: (p) => <Shield {...p} />,
  crown: (p) => <Crown {...p} />,
};

const s = StyleSheet.create({
  monogram: {
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontWeight: '900',
    color: '#ffffff',
    letterSpacing: -1,
  },
  emblemWrap: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
  },
});

interface WorldCupCrestPreviewProps {
  colorId: CrestColorId;
  emblemId: CrestEmblemId;
  name: string;
  size?: number;
}

export function WorldCupCrestPreview({ colorId, emblemId, name, size = 88 }: WorldCupCrestPreviewProps) {
  const h = Math.round(size * 1.05);
  const mTop = Math.round(h * 0.08);
  const mFont = Math.round(size * 0.26);
  const eTop = Math.round(h * 0.52);
  const eSize = Math.round(size * 0.34);

  const crestColor = CREST_COLORS.find((c) => c.id === colorId) ?? CREST_COLORS[0]!;
  const monogram = useMemo(
    () =>
      name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] ?? '')
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'SQ',
    [name],
  );
  const EmblemIcon = EMBLEM_ICONS[emblemId];

  return (
    <View style={{ width: size, height: h }}>
      <Svg viewBox="0 0 100 105" width={size} height={h}>
        <Defs>
          <SvgLinearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor={crestColor.from} />
            <Stop offset="1" stopColor={crestColor.to} />
          </SvgLinearGradient>
          <SvgLinearGradient id="cg-hi" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#ffffff" stopOpacity="0.45" />
            <Stop offset="0.4" stopColor="#ffffff" stopOpacity="0" />
          </SvgLinearGradient>
        </Defs>
        <Path
          d="M50 2 L92 14 L88 60 Q85 86 50 102 Q15 86 12 60 L8 14 Z"
          fill="url(#cg)"
          stroke="rgba(0,0,0,0.4)"
          strokeWidth="1.2"
        />
        <Path d="M50 5 L88 16 L84 28 L50 22 L16 28 L12 16 Z" fill="url(#cg-hi)" />
        <Path d="M14 44 Q50 56 86 44 L86 50 Q50 62 14 50 Z" fill="rgba(0,0,0,0.18)" />
      </Svg>
      <Text style={[s.monogram, { top: mTop, fontSize: mFont }]}>{monogram}</Text>
      <View style={[s.emblemWrap, { top: eTop }]}>
        <EmblemIcon size={eSize} color="#ffffff" strokeWidth={1.8} />
      </View>
    </View>
  );
}
